import React, { ReactNode, useEffect, useRef } from "react";
import styled from "styled-components/macro";

import StudentNavSection from "../StudentNavSection";
import StudentNavIllustration from "../StudentNavIllustration";
import AlongLogo from "components/AlongLogo";
import AlongMonogram from "images/along-monogram.svg";
import WarningIcon from "ui/Icons/Warning";
import { $BaseLink } from "ui/Link";
import SunnySpinner from "ui/SunnySpinner";
import { theme } from "ui/theme";
import Typography from "ui/Typography";

import { StudentNavReflectionFragment } from "../StudentNavigation.generated";

import { ReflectionType } from "../getReflectionTypeIcon";
import { hexColorWithAlpha, transition } from "helpers/css";
import NavigationDropdown from "components/NavigationDropdown";
import Tooltip from "ui/Tooltip";

const $Expanded = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  padding: 1rem;
`;

const $Header = styled.div`
  border-bottom: 1px solid ${theme.ColorSpace100};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 1rem;
`;

const $Logo = styled(AlongLogo).attrs({
  size: "large",
  variant: "bulb-mint",
})`
  max-width: 150px;
  margin-bottom: 1.5rem;
`;

const $Greeting = styled(Typography).attrs({
  size: "bodySansSerif",
})`
  margin: 0.5rem 0 0.25rem;
`;

const $Stats = styled(Typography).attrs({
  color: "ColorSpace500",
  size: "tooltip",
})`
  margin-bottom: 0.75rem;
`;

const $ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const $WarningIcon = styled(WarningIcon)`
  margin: 0 auto 0.5rem;
`;

const $Collapsed = styled($Expanded)`
  align-items: center;
  padding-top: 2rem;
  overflow: visible;
`;

const $CollapsedChild = styled.div`
  &:not(:last-child) {
    margin-bottom: 2rem;
  }
`;

const $Illustration = styled(StudentNavIllustration)`
  ${transition("filter 200ms ease-in-out")}
`;

const $SectionLink = styled($BaseLink)`
  display: block;
  position: relative;

  &:focus-visible {
    outline-color: ${theme.ColorBlurple500};
  }

  &:active,
  &:focus,
  &:hover {
    text-decoration: none;

    ${$Illustration} {
      // Increase the size of the drop-shadow from 1px to 3px.
      filter: drop-shadow(
        0 3px 0 ${hexColorWithAlpha(theme.ColorSpace500, 0.4)}
      );
    }
  }
`;

const $AlongMonogram = styled.img.attrs({
  alt: "Along",
  src: AlongMonogram,
})`
  width: 4.75rem;
`;

interface StudentNavMenuContentProps {
  children: ReactNode;
  hasError: boolean;
  isLoading: boolean;
}

function StudentNavMenuContent({
  children,
  hasError,
  isLoading,
}: StudentNavMenuContentProps) {
  if (isLoading) {
    return <SunnySpinner />;
  }

  if (hasError) {
    return (
      <$ErrorContainer>
        <$WarningIcon role="presentation" />
        <Typography size="bodySansSerif">
          Sorry, we couldn’t get your questions. Please refresh the page or try
          again later.
        </Typography>
      </$ErrorContainer>
    );
  }

  // Use fragment to return JSX Element
  // eslint-disable-next-line
  return <>{children}</>;
}

export interface StudentNavMenuProps {
  focusedSection: ReflectionType | null;
  hasError: boolean;
  isLoading: boolean;
  isOpen: boolean;
  menuId: string;
  onToggleNav: (section: ReflectionType | null) => void;
  sections: Array<{
    defaultExpanded?: boolean;
    hasNotifications?: boolean;
    reflections: Array<StudentNavReflectionFragment | null>;
    type: ReflectionType;
  }>;
  stats: {
    allSessionsReceived: number;
    allSessionsResponded: number;
  };
  student: {
    id: string;
    name: string;
    isDemo: boolean;
    profileImage?: { id: string; url: string } | null;
  };
}

export default function StudentNavMenu({
  focusedSection,
  hasError,
  isLoading,
  isOpen,
  menuId,
  onToggleNav,
  sections,
  stats,
  student,
}: StudentNavMenuProps) {
  const sectionRefs = useRef<{
    [section in ReflectionType]?: HTMLAnchorElement | null;
  }>({});

  useEffect(() => {
    if (focusedSection) {
      sectionRefs.current[focusedSection]?.focus();
    }
  }, [focusedSection]);

  return isOpen ? (
    <$Expanded>
      <$Header>
        <$Logo />
        <NavigationDropdown isOpen student={student} />
        <$Greeting>Welcome back, {student.name}</$Greeting>
        <$Stats>
          {stats.allSessionsReceived || 0} questions received •{" "}
          {stats.allSessionsResponded || 0} answered
        </$Stats>
      </$Header>
      <StudentNavMenuContent hasError={hasError} isLoading={isLoading}>
        {sections.map((section) => (
          <StudentNavSection
            {...section}
            headerRef={(el) => {
              sectionRefs.current[section.type] = el;
            }}
            key={section.type}
          />
        ))}
      </StudentNavMenuContent>
    </$Expanded>
  ) : (
    <$Collapsed>
      <$CollapsedChild>
        <$AlongMonogram />
      </$CollapsedChild>
      <$CollapsedChild>
        <NavigationDropdown isOpen={isOpen} student={student} />
      </$CollapsedChild>
      {sections.map(({ hasNotifications, type }) => (
        <$CollapsedChild key={type}>
          <Tooltip label={`${type} questions`}>
            <$SectionLink
              aria-controls={menuId}
              aria-expanded={isOpen}
              analyticsName="clicked_expand_nav"
              onClick={() => onToggleNav(type)}
              aria-label={`${type} questions`}
            >
              <$Illustration hasNotifications={hasNotifications} type={type} />
            </$SectionLink>
          </Tooltip>
        </$CollapsedChild>
      ))}
    </$Collapsed>
  );
}
